import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, MessageCircle, IndianRupee, X, Loader2, Send } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { openPaymentPage } from "@/lib/whatsapp";
import { payBookingAdvance } from "@/lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-tealbrand/50 focus:border-tealbrand transition";

const PLANS = [
  {
    service: "Personal Caretaker & Attendant",
    description: "Daily living assistance, hygiene, feeding, mobility & companionship.",
    perDay: "₹1,000 – ₹1,500",
    perMonth: "₹22,000 – ₹32,000",
    badge: "Popular for Seniors",
    featured: false,
  },
  {
    service: "Nursing Care at Home",
    description: "GNM & B.Sc nurses — wound care, IV infusions, injections, catheter & vitals.",
    perDay: "₹2,500 – ₹4,000",
    perMonth: "₹35,000 – ₹65,000",
    badge: "Clinical Expertise",
    featured: true,
  },
  {
    service: "ICU at Home & Critical Care",
    description: "24/7 ICU-trained nursing, ventilator, tracheostomy & bi-pap support.",
    perDay: "₹4,500 – ₹7,500",
    perMonth: "₹1,25,000 – ₹1,95,000",
    badge: "24/7 Critical Care",
    featured: false,
  },
  {
    service: "Baby Caretaker & Japa Maid",
    description: "Postpartum mother care, traditional massage, newborn hygiene & sleep schedules.",
    perDay: "₹1,800 – ₹3,000",
    perMonth: "₹38,000 – ₹60,000",
    badge: "Mother & Newborn",
    featured: false,
  },
];

const Pricing = () => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [quoteService, setQuoteService] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ name: "", phone: "", city: "" });
  const [quoteSending, setQuoteSending] = useState(false);

  const submitQuote = async (e) => {
    e.preventDefault();
    setQuoteSending(true);
    try {
      await axios.post(`${API}/leads`, {
        type: "quote_request",
        data: {
          Service: quoteService,
          "Full Name": quoteForm.name,
          Phone: quoteForm.phone,
          "Location / City": quoteForm.city,
        },
      });
      toast.success("Quote request received! Our team will contact you with exact pricing shortly.");
      setQuoteService(null);
      setQuoteForm({ name: "", phone: "", city: "" });
    } catch {
      toast.error("Could not submit right now. Please call us at +91-9211949414.");
    } finally {
      setQuoteSending(false);
    }
  };

  return (
  <section id="pricing" className="py-16 md:py-24 bg-white border-y border-slate-200" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <SectionHeading
        number="02 — Transparent Pricing"
        title="Honest rates,"
        accent="no hidden charges."
        subtitle="Indicative daily and monthly ranges. Your final quote is confirmed after a free patient assessment."
        id="pricing"
      />

      <div className="mb-8">
        <label className="inline-flex items-start gap-2.5 cursor-pointer bg-alabaster border border-slate-200 rounded-2xl px-5 py-3.5 max-w-xl" data-testid="pricing-terms-accept-label">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            data-testid="pricing-terms-accept-checkbox"
            className="mt-1 h-4 w-4 accent-tealbrand shrink-0"
          />
          <span className="text-sm text-slate-600 leading-relaxed">
            I have read and accept the{" "}
            <Link to="/terms" className="text-tealbrand-dark font-semibold underline" data-testid="pricing-terms-accept-link">
              Terms of Service
            </Link>
            , including the advance payment and zero-cancellation policy.
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        {PLANS.map((plan, i) => (
          <motion.div
            key={plan.service}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`relative rounded-3xl p-7 flex flex-col border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
              plan.featured
                ? "bg-navy text-white border-tealbrand/40 shadow-[0_0_35px_rgba(13,148,136,0.18)]"
                : "bg-alabaster border-slate-200 hover:border-tealbrand/40"
            }`}
            data-testid={`pricing-card-${i}`}
          >
            <span
              className={`self-start inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 rounded-full mb-6 ${
                plan.featured ? "bg-tealbrand text-white" : "bg-tealbrand-light text-tealbrand-dark"
              }`}
            >
              <BadgeCheck className="h-3.5 w-3.5" />
              {plan.badge}
            </span>
            <h3 className={`font-display text-xl font-semibold leading-snug mb-3 ${plan.featured ? "text-white" : "text-navy"}`}>
              {plan.service}
            </h3>
            <p className={`text-sm leading-relaxed mb-8 flex-1 ${plan.featured ? "text-slate-300" : "text-slate-600"}`}>
              {plan.description}
            </p>
            <div className={`space-y-3 border-t pt-6 mb-8 ${plan.featured ? "border-white/15" : "border-slate-200"}`}>
              <div className="flex items-baseline justify-between">
                <span className={`text-xs uppercase tracking-wider font-semibold ${plan.featured ? "text-slate-400" : "text-slate-500"}`}>Per day</span>
                <span className={`font-display text-lg font-bold ${plan.featured ? "text-tealbrand-light" : "text-navy"}`} data-testid={`pricing-per-day-${i}`}>
                  {plan.perDay}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className={`text-xs uppercase tracking-wider font-semibold ${plan.featured ? "text-slate-400" : "text-slate-500"}`}>Per month</span>
                <span className={`font-display text-lg font-bold ${plan.featured ? "text-tealbrand-light" : "text-navy"}`} data-testid={`pricing-per-month-${i}`}>
                  {plan.perMonth}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => setQuoteService(plan.service)}
                data-testid={`pricing-quote-btn-${i}`}
                className={`inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-full text-sm transition-colors duration-300 ${
                  plan.featured
                    ? "bg-tealbrand text-white hover:bg-white hover:text-navy"
                    : "bg-navy text-white hover:bg-tealbrand"
                }`}
              >
                <MessageCircle className="h-4 w-4" /> Get Exact Quote
              </button>
              <button
                disabled={!termsAccepted}
                onClick={() =>
                  payBookingAdvance({
                    termsAccepted,
                    onSuccess: () => toast.success("₹500 booking advance received — payment verified!"),
                    onFailure: () => toast.error("Payment not completed. You can retry anytime via Pay Now."),
                  })
                }
                data-testid={`pricing-pay-btn-${i}`}
                className={`inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-full text-sm border-2 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                  plan.featured
                    ? "border-tealbrand-light/60 text-tealbrand-light hover:bg-tealbrand-light hover:text-navy"
                    : "border-navy/15 text-navy hover:border-tealbrand hover:text-tealbrand"
                }`}
              >
                <IndianRupee className="h-4 w-4" /> Book &amp; Pay
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mt-10 text-sm text-slate-500 max-w-3xl"
        data-testid="pricing-disclaimer"
      >
        * Prices are indicative ranges. Final pricing depends on patient condition, shift
        duration (12 hr / 24 hr live-in) and location, and is confirmed after a free
        assessment by our care team. Book &amp; Pay secures your slot instantly with a fully
        adjustable ₹500 booking advance.
      </motion.p>

      {quoteService && (
        <div className="fixed inset-0 z-[60] bg-navy/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="quote-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-3xl w-full max-w-md p-8 relative shadow-2xl"
          >
            <button
              onClick={() => setQuoteService(null)}
              data-testid="quote-close-btn"
              aria-label="Close"
              className="absolute top-5 right-5 text-slate-400 hover:text-navy transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-tealbrand mb-2">Exact Quote</p>
            <h3 className="font-display text-xl font-bold text-navy mb-1" data-testid="quote-service-name">{quoteService}</h3>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Share your details — our care team will send your exact quote by email or call shortly.
            </p>
            <form className="space-y-4" onSubmit={submitQuote}>
              <input
                required
                placeholder="Full name"
                data-testid="quote-name-input"
                className={inputCls}
                value={quoteForm.name}
                onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
              />
              <input
                required
                type="tel"
                placeholder="Phone number"
                data-testid="quote-phone-input"
                className={inputCls}
                value={quoteForm.phone}
                onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
              />
              <input
                required
                placeholder="Location / City"
                data-testid="quote-city-input"
                className={inputCls}
                value={quoteForm.city}
                onChange={(e) => setQuoteForm({ ...quoteForm, city: e.target.value })}
              />
              <button
                type="submit"
                disabled={quoteSending}
                data-testid="quote-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-3.5 rounded-full hover:bg-tealbrand transition-colors disabled:opacity-60"
              >
                {quoteSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Get My Exact Quote
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  </section>
  );
};

export default Pricing;
